import { AnimatePresence, motion } from 'framer-motion';
import Delta from 'quill-delta';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useMarkdownConverter } from '@treams/treams-hooks';
import {
	TranscriptionStatus,
	useRegenerateTranscriptionSummaryMutation,
	useUpdateTranscriptionSummaryMutation,
} from '@treams/treams-network';
import {
	AICard,
	Button,
	CheckmarkIcon,
	FlexGap,
	Paragraph,
	PencilEditBoxIcon,
	PlaybackPlayIcon,
	PlaybackStopIcon,
	QuillInput,
	QuillText,
	Skeleton,
} from '@treams/treams-ui';

import { TranscriptionFollowups } from './TranscriptionFollowups';
import type { TranscriptionFollowupsData } from './TranscriptionModule';

type TranscriptionData = {
	id: string;
	status: TranscriptionStatus;
	summary?: string | null;
	editableSummary?: Delta;
	followups?: TranscriptionFollowupsData | null;
};

export type StreamingSummaryActions = {
	/** Omitted when the transcription cannot be continued, e.g. it has uploaded audio. */
	onContinue?: () => void;
	continueDisabled?: boolean;
	/** Generates the first summary of a streamed transcript. */
	onFinish?: () => void;
};

type Props = {
	transcription: TranscriptionData;
	transcribeAgain: () => void;
	viewerCanRecord: boolean;
	/** Whether the viewer can edit the summary and act on its follow-ups. */
	viewerCanEditSummary?: boolean;
	/** Actions for streamed transcriptions (Continue and Finish). */
	streamingActions?: StreamingSummaryActions;
};

export const TranscriptionSummary = ({
	transcription,
	transcribeAgain,
	viewerCanRecord,
	viewerCanEditSummary = true,
	streamingActions,
}: Props) => {
	const { t } = useTranslation();
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState<Delta | undefined>(undefined);

	const { markdownToQuillDelta } = useMarkdownConverter();

	const [updateSummary, { loading: saving }] = useUpdateTranscriptionSummaryMutation();
	const [regenerateSummary, { loading: regenerating }] =
		useRegenerateTranscriptionSummaryMutation();

	const isLoading =
		transcription.status === TranscriptionStatus.Pending ||
		transcription.status === TranscriptionStatus.Processing;

	const summaryFailed = transcription.status === TranscriptionStatus.SummaryFailed;
	const hasSummary = !!transcription.editableSummary || !!transcription.summary;
	const hasFollowups =
		(transcription.followups?.goalSuggestions.length ?? 0) > 0 ||
		(transcription.followups?.feedbackRequestSuggestions.length ?? 0) > 0;

	const startEditing = useCallback(() => {
		const initial =
			transcription.editableSummary ??
			(transcription.summary ? markdownToQuillDelta(transcription.summary) : new Delta());

		setDraft(initial);
		setEditing(true);
	}, [transcription.editableSummary, transcription.summary, markdownToQuillDelta]);

	const cancelEditing = useCallback(() => {
		setEditing(false);
		setDraft(undefined);
	}, []);

	const save = useCallback(async () => {
		if (!draft) return;

		await updateSummary({
			variables: {
				input: {
					transcriptionID: transcription.id,
					editableSummary: draft,
				},
			},
		});

		setEditing(false);
		setDraft(undefined);
	}, [draft, transcription.id, updateSummary]);

	const retrySummary = useCallback(async () => {
		await regenerateSummary({
			variables: {
				input: { transcriptionID: transcription.id },
			},
		});
	}, [regenerateSummary, transcription.id]);

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0, height: 0 }}
				animate={{ opacity: 1, height: 'auto' }}
				exit={{ opacity: 0, height: 0 }}
				transition={{ duration: 0.2 }}
			>
				<AICard
					title={t('transcription.summary')}
					actions={
						<>
							{!isLoading && hasSummary && !editing && (
								<FlexGap gap={8} flexDirection={['column', 'row']}>
									{viewerCanRecord && streamingActions?.onContinue && (
										<Button
											title={t('transcription.streaming.continue')}
											type="secondary"
											small
											icon={<PlaybackPlayIcon />}
											onClick={streamingActions.onContinue}
											disabled={saving || streamingActions.continueDisabled}
										/>
									)}
									{viewerCanRecord && !streamingActions && (
										<Button
											title={t('transcription.transcribeAgain')}
											type="ai"
											small
											onClick={transcribeAgain}
											disabled={saving}
										/>
									)}
									{viewerCanEditSummary && (
										<Button
											title={t('transcription.editSummary')}
											type="secondary"
											small
											icon={<PencilEditBoxIcon />}
											onClick={startEditing}
										/>
									)}
								</FlexGap>
							)}
						</>
					}
				>
					{isLoading && (
						<Box minWidth="600px">
							<Skeleton.Repeat count={3}>
								<Skeleton.Box width="100%" height={20} mb="8px" />
							</Skeleton.Repeat>
						</Box>
					)}
					{!isLoading && editing ? (
						<FlexGap gap={8} flexDirection="column" p={2} style={{ backgroundColor: 'white' }}>
							<QuillInput value={draft} onChange={setDraft} minHeight={80} />
							<FlexGap gap={8} justifyContent="flex-end">
								<Button
									title={t('common.cancel')}
									type="secondary"
									small
									onClick={cancelEditing}
									disabled={saving}
								/>
								<Button
									title={t('common.save')}
									type="primary"
									small
									icon={<CheckmarkIcon />}
									onClick={save}
									disabled={saving}
								/>
							</FlexGap>
						</FlexGap>
					) : (
						!isLoading &&
						hasSummary && (
							<>
								{transcription.editableSummary ? (
									<QuillText value={transcription.editableSummary} />
								) : transcription.summary ? (
									<div>{transcription.summary}</div>
								) : (
									<div>{t('transcription.noSummaryAvailable')}</div>
								)}
								{!isLoading && !editing && hasFollowups && viewerCanEditSummary && (
									<TranscriptionFollowups
										transcriptionID={transcription.id}
										followups={transcription.followups!}
									/>
								)}
							</>
						)
					)}
					{!isLoading && !editing && !hasSummary && summaryFailed && (
						<FlexGap gap={12} flexDirection="column" alignItems="flex-start">
							<Paragraph small>{t('transcription.summaryGenerationFailed')}</Paragraph>
							{viewerCanRecord && (
								<FlexGap gap={8} flexDirection={['column', 'row']}>
									<Button
										title={t('transcription.regenerateSummary')}
										type="ai"
										small
										onClick={retrySummary}
										disabled={regenerating}
										loading={regenerating}
									/>
									{streamingActions?.onContinue && (
										<Button
											title={t('transcription.streaming.continue')}
											type="secondary"
											small
											icon={<PlaybackPlayIcon />}
											onClick={streamingActions.onContinue}
											disabled={regenerating || streamingActions.continueDisabled}
										/>
									)}
								</FlexGap>
							)}
						</FlexGap>
					)}
					{!isLoading && !editing && !hasSummary && !summaryFailed && streamingActions && (
						<FlexGap gap={12} flexDirection="column" alignItems="flex-start">
							<Paragraph small>{t('transcription.streaming.noSummaryYet')}</Paragraph>
							{viewerCanRecord && (
								<FlexGap gap={8} flexDirection={['column', 'row']}>
									{streamingActions.onFinish && (
										<Button
											title={t('transcription.streaming.finish')}
											type="primary"
											small
											icon={<PlaybackStopIcon />}
											onClick={streamingActions.onFinish}
											disabled={streamingActions.continueDisabled}
										/>
									)}
									{streamingActions.onContinue && (
										<Button
											title={t('transcription.streaming.continue')}
											type="secondary"
											small
											icon={<PlaybackPlayIcon />}
											onClick={streamingActions.onContinue}
											disabled={streamingActions.continueDisabled}
										/>
									)}
								</FlexGap>
							)}
						</FlexGap>
					)}
				</AICard>
			</motion.div>
		</AnimatePresence>
	);
};
